// WhatsApp transcript grammar, ported from whatsapp_parser.py at the repo root.
//
// Two grammars, not one. Android writes
//
//     23/09/2025 14:03 - Nara: hello
//     IMG-20250923-WA0001.jpg (file attached)
//
// and iPhone writes
//
//     [23/09/2025, 14:03:12] Nara: hello
//     <attached: 00000042-PHOTO-2025-09-23-14-04-01.jpg>
//
// — square brackets, no " - ", seconds, a different attachment syntax, and
// invisible bidi marks (U+200E) in front of lines and attachments. Supporting
// only the first means an iPhone export parses to *nothing*, with no error:
// every line fails the date match and becomes a continuation of a message that
// never started. That is how it failed for the first person outside this
// project who tried it.
//
// The fixtures are pt-BR, so `arquivo anexado` matters as much as
// `file attached`. WhatsApp also emits a narrow no-break space before AM/PM in
// some locales, which is the classic reason a parser silently drops a day.

const NNBSP = '\u202f';
/** Left-to-right and right-to-left marks: invisible, and fatal to a regex. */
const BIDI = /[\u200e\u200f\u061c]/g;

const ANDROID_RE = new RegExp(
  // The comma after the date is locale-dependent — some Android exports have
  // it, some do not, and the ones that do not parsed to nothing.
  '^(\\d{1,2})/(\\d{1,2})/(\\d{2,4}),? ' +
  '(\\d{1,2}):(\\d{2})(?::\\d{2})?' +
  `(?:[\\s${NNBSP}\\u00a0]*([APap]\\.?[Mm]\\.?))? - ` +
  '(.*)$'
);

const IOS_RE = new RegExp(
  '^\\[(\\d{1,2})/(\\d{1,2})/(\\d{2,4}),? ' +
  '(\\d{1,2}):(\\d{2})(?::\\d{2})?' +
  `(?:[\\s${NNBSP}\\u00a0]*([APap]\\.?[Mm]\\.?))?\\]\\s*` +
  '(.*)$'
);

const SENDER_RE = /^([^:\n]{1,64}): ?([\s\S]*)$/;

/** Minúscula e sem o ponto. `''` quando não há extensão nenhuma. */
const extensionOf = (n: string) => {
  const dot = n.lastIndexOf('.');
  return dot < 0 ? '' : n.slice(dot + 1).toLowerCase();
};

/**
 * Android: `IMG-0001.jpg (file attached)`. iPhone: `<attached: IMG_0001.jpg>`.
 *
 * Sem âncoras, pela mesma razão que o do iPhone não as tem — e esta ficou
 * ancorada quando aquela foi corrigida, o que é o mesmo defeito deixado numa
 * gramática só.
 *
 * O marcador raramente é o corpo inteiro. Um documento leva o nome legível
 * na linha seguinte, porque o que o Android põe no marcador é um UUID:
 *
 *     1eb462d7-…-803b725861f8.pdf (file attached)
 *     PPS_ GLÊNIO_TAMBORÉ 2.pdf
 *
 * e uma foto com legenda leva a legenda na linha seguinte. Com o `$` ali, o
 * corpo de duas linhas não casava e a mensagem virava texto cru — o anexo
 * perdia o content id e o ficheiro ficava no zip sem ninguém a reclamá-lo.
 *
 * Medido no export de 70 MB que serve de fixture: 62 dos 277 anexos
 * desapareciam assim — os 22 PDFs, os 2 docx, e 37 das 120 fotografias,
 * exatamente as que tinham legenda. 22% do arquivo, sem um erro em sítio
 * nenhum.
 */
const ATTACH_RE = /(.+?) \((?:file attached|arquivo anexado)\)/;
/**
 * Sem âncoras, porque o marcador nem sempre é a linha inteira.
 *
 * Para uma foto o iPhone escreve só `<attached: 0001-PHOTO.jpg>`. Para um
 * documento escreve o nome e a contagem de páginas antes dele:
 * `Orçamento.pdf • 1 page <attached: 00000280-Orçamento.pdf>`. Com o `^`
 * que estava aqui, nenhum documento era reconhecido — todos os PDFs
 * apareciam como texto cru, com o marcador à vista.
 */
const IOS_ATTACH_RE = /<(?:attached|anexado):\s*([^>]+)>/;
/**
 * `Orçamento.pdf • 1 page` — o rótulo que o iPhone põe antes do marcador de
 * um documento. É o nome do ficheiro outra vez, e a bolha já o mostra.
 *
 * As marcas bidi entram na conta porque o iPhone semeia-as no meio disto:
 * a linha real é `ticket.pdf • ‎2 pages ‎<attached: …>`, com um U+200E antes
 * do número e outro antes do marcador. `\s` não os apanha — são caracteres
 * de formatação, não espaço — e sem isto o rótulo sobrevivia como legenda.
 */
const BIDI_CLASS = '[\u200e\u200f\u061c]';
const DOC_LABEL_RE = new RegExp(
  `^.+?${BIDI_CLASS}*\\s*[•·]${BIDI_CLASS}*\\s*${BIDI_CLASS}*\\d+\\s*`
  + `(?:pages?|p\u00e1ginas?|p\u00e1gina)${BIDI_CLASS}*$`, 'i');

/**
 * "Without media" exports carry a marker where the file would be — iOS writes
 * `image omitted`, Android writes `<Media omitted>`, and pt-BR writes
 * `imagem ocultada`. Showing that as message text makes a deliberate choice at
 * export time look like a bug at import time, so it is recognised and named.
 */
const OMITTED_RE = new RegExp(
  '^(?:<)?(?:'
  + '(image|photo|imagem|foto)|(video|v\u00eddeo)|(audio|\u00e1udio|voice message|mensagem de voz)'
  + '|(sticker|figurinha)|(gif)|(document|documento)|(media|m\u00eddia)'
  + ')\\s*(?:omitted|omitida|omitido|ocultada|ocultado|oculta|n\u00e3o inclu\u00eddo)(?:>)?$',
  'i',
);
const OMITTED_KIND = ['image', 'video', 'audio', 'sticker', 'gif', 'document', 'media'];
const EDITED = '<This message was edited>';

const SYSTEM_RE = new RegExp([
  'end-to-end encrypted', 'created group', '^You were added', '\\badded\\b',
  '\\bleft$', '\\bremoved\\b', 'changed the group name', 'changed the subject',
  "changed this group's icon", 'changed their phone number',
  'criou o grupo', 'adicionou', 'saiu', 'removeu',
  'As mensagens e as chamadas são protegidas',
].join('|'));

export interface ParsedMessage {
  seq: number; ts: number; kind: 0 | 1 | 2;
  sender?: string; body?: string; file?: string; attach?: string;
  /**
   * O nome do ficheiro, quando ele é um documento.
   *
   * Para uma foto não serve de nada: a bolha mostra a foto. Para um PDF é
   * quase tudo o que há — sem ele a bolha era um link a dizer
   * "attachment", e quem o recebeu não sabia o que estava a abrir.
   */
  name?: string;
  /** The export left this file out: 'image', 'video', 'audio', … */
  omitted?: string;
}

export function parseTranscript(text: string): { messages: ParsedMessage[]; attachments: Set<string> } {
  // A bare CR is a line break too — WhatsApp emits them, and treating one as
  // ordinary text silently merges the next message into its predecessor.
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const messages: ParsedMessage[] = [];
  const attachments = new Set<string>();
  let current: { ts: number; sender: string | null; kind: 0 | 1 | 2; bodyLines: string[] } | null = null;

  const flush = () => {
    if (!current) return;
    let body = current.bodyLines.join('\n').trim();
    if (body.endsWith(EDITED)) body = body.slice(0, -EDITED.length).trim();

    const m = ATTACH_RE.exec(body) ?? IOS_ATTACH_RE.exec(body);
    const omitted = m ? null : OMITTED_RE.exec(body);
    const msg: ParsedMessage = { seq: messages.length, ts: current.ts, kind: current.kind };
    if (current.sender) msg.sender = current.sender;
    if (m) {
      msg.kind = 2;
      msg.file = m[1]!.trim();
      attachments.add(msg.file);
      // O que sobra à volta do marcador é a legenda, quando há uma. Num
      // documento não há: o que vem antes é o nome do ficheiro e o número
      // de páginas, e repeti-lo por baixo da bolha não diz nada de novo.
      const rest = body.replace(m[0], ' ').replace(/\s+/g, ' ').trim();
      // A sobra é o nome do documento quando tem a extensão do próprio
      // anexo, e legenda em qualquer outro caso.
      //
      // No Android o marcador é um UUID, portanto sem isto o cartão do PDF
      // viria rotulado `1eb462d7-2f5d-4da2-a177-803b725861f8.pdf` — tão útil
      // como não vir. O nome que a pessoa reconhece está ali, uma linha
      // abaixo. A extensão igual é o que distingue um nome de uma legenda:
      // a legenda de uma foto não acaba em `.jpg`.
      if (rest && extensionOf(rest) === extensionOf(msg.file)) msg.name = rest;
      else if (rest && !DOC_LABEL_RE.test(rest)) msg.body = rest;
    } else if (omitted) {
      const which = OMITTED_KIND[omitted.slice(1, 8).findIndex(Boolean)] ?? 'media';
      msg.omitted = which;
    } else if (body) {
      msg.body = body;
    }
    messages.push(msg);
    current = null;
  };

  // WhatsApp writes whatever order the phone is set to, and the file never says
  // which. `9/18/26` is unambiguous — 18 cannot be a month — but `03/09` is
  // not, so the whole transcript is scanned first and the answer applied to
  // every line. Guessing per line would put two messages from one conversation
  // in different years.
  //
  // This is not cosmetic: a real iPhone export read day-first landed every
  // message in June 2027, because month 18 overflows.
  const matched: Array<{ m: RegExpExecArray; line: string }> = [];
  const parsed: Array<{ m: RegExpExecArray | null; line: string }> = [];
  for (const raw of lines) {
    // Strip bidi marks before matching: iPhone puts U+200E in front of a line
    // and in front of every attachment, and an invisible character is still a
    // character to a regex.
    const line = raw.replace(BIDI, '');
    const m = ANDROID_RE.exec(line) ?? IOS_RE.exec(line);
    if (m) matched.push({ m, line });
    parsed.push({ m, line });
  }

  let monthFirst = false;
  for (const { m } of matched) {
    const a = parseInt(m[1]!, 10), b = parseInt(m[2]!, 10);
    if (a > 12) { monthFirst = false; break; }   // first number is a day
    if (b > 12) { monthFirst = true; break; }    // second number is a day
  }

  for (const { m, line } of parsed) {
    if (!m) {
      if (current) current.bodyLines.push(line);
      continue;
    }
    flush();
    const [, n1, n2, y, hh, mm, ampm, rest] = m as unknown as string[];

    let hour = parseInt(hh!, 10);
    if (ampm) {
      const pm = /p/i.test(ampm);
      if (pm && hour !== 12) hour += 12;
      if (!pm && hour === 12) hour = 0;
    }
    const year = y!.length === 2 ? 2000 + parseInt(y!, 10) : parseInt(y!, 10);
    const day = parseInt(monthFirst ? n2! : n1!, 10);
    const month = parseInt(monthFirst ? n1! : n2!, 10);
    const ts = Date.UTC(year, month - 1, day, hour, parseInt(mm!, 10));

    // A linha de sistema é reconhecida pelo *começo*, não pelo conteúdo.
    //
    // Testar a linha inteira rebaixava a mensagem de quem escrevesse "the
    // tall units on the left" — `\bleft$` existe para "Ana left" e pegava
    // qualquer frase terminada na palavra. O mesmo valia para "added" e
    // "removed", que são palavras comuns.
    //
    // O que separa os dois casos é o prefixo: "Ana: texto" é mensagem,
    // "Ana left" não tem dois-pontos. O SYSTEM_RE segue necessário porque
    // algumas linhas de sistema têm dois-pontos — "You changed the subject
    // to: Kitchen" — e aí é o *remetente aparente* que a denuncia.
    const s = SENDER_RE.exec(rest!);
    if (s && !SYSTEM_RE.test(s[1]!)) {
      current = { ts, sender: s[1]!.trim(), kind: 0, bodyLines: [s[2]!] };
    } else {
      current = { ts, sender: null, kind: 1, bodyLines: [rest!] };
    }
  }
  flush();
  return { messages, attachments };
}

/**
 * The one system line worth dropping.
 *
 * WhatsApp opens every export with the end-to-end notice, in the phone's
 * language. It costs tokens and carries nothing. Everything else on a system
 * line is a fact about the conversation — who was added, who was removed, who
 * left, who changed the subject or the icon — and dropping those wholesale is
 * how "Glênio was removed from this group" became information the archive
 * held and nothing could see.
 */
const BOILERPLATE = [
  /end-to-end encrypted/i,
  /criptografad[ao]s? de ponta a ponta/i,
  /cifrad[ao]s? de extremo a extremo/i,
  /chiffrés? de bout en bout/i,
];
export const isBoilerplate = (body?: string) =>
  !!body && BOILERPLATE.some((re) => re.test(body));
